import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SunLogo, { AppleGlyph, PlayGlyph } from "../components/SunLogo";
import AppPreview from "../components/AppPreview";
import FaqSection, { AnswerSection } from "../components/FaqSection";
import Seo, {
  appLd,
  faqLd,
  organizationLd,
  websiteLd,
} from "../components/Seo";
import { homeFaq } from "../seoContent";
import { site, tiles } from "../mock";
import SocialLinks from "../components/SocialLinks";
import { track } from "../analytics";

const Landing = () => {
  const [storeMsg, setStoreMsg] = useState("");

  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  const onStoreClick = (store) => {
    track(store === "apple" ? "click_app_store" : "click_google_play", {
      label: store === "apple" ? "App Store" : "Google Play",
    });
    if (store === "apple" && !site.appStoreUrl) {
      setStoreMsg(
        "Coming soon on the App Store \u2014 we'll announce the launch right here."
      );
    }
  };

  return (
    <>
    <Seo
      title="Ingredient Checker: Scan Any Label and See What Is Inside | InZoRAH"
      description="A free ingredient checker for food, drinks, supplements and skincare. InZoRAH is a food ingredient scanner and ingredient analyzer that reads the barcode or label and explains every ingredient in plain English, with real USDA and FDA sources."
      path="/"
      keywords="ingredient checker, food ingredient scanner, ingredient analyzer, is this ingredient safe, what ingredients are in, read food labels, skincare ingredients, ingredient list explained"
      jsonLd={[organizationLd, websiteLd, appLd, faqLd(homeFaq)]}
    />
    <main className="inz-main">
      <nav className="inz-nav" aria-label="Primary">
        <Link
          to="/ingredients"
          onClick={() => track("click_ingredients_az", { label: "header" })}
        >
          Ingredients A-Z
        </Link>
        <Link
          to="/ingredient"
          onClick={() => track("click_ingredient_blog", { label: "header" })}
        >
          Ingredient blog
        </Link>
      </nav>
      <SunLogo />

      <p className="inz-word">{site.brand}</p>
      <p className="inz-kicker">{site.kicker}</p>

      <h1 className="inz-h1">{site.headline}</h1>
      <p className="inz-lede">{site.lede}</p>

      <div className="inz-cta">
        <button
          className="inz-badge"
          type="button"
          onClick={() => onStoreClick("apple")}
          data-testid="app-store-button"
        >
          <AppleGlyph />
          <span>
            <span className="inz-small">Coming soon on</span>App Store
          </span>
        </button>
        <a
          className="inz-badge is-live"
          href={site.playUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => onStoreClick("google")}
          data-testid="google-play-button"
        >
          <PlayGlyph />
          <span>
            <span className="inz-small">Get it on</span>Google Play
          </span>
        </a>
      </div>
      <SocialLinks />
      <p className="inz-hint" role="status" aria-live="polite">
        {storeMsg || site.hint}
      </p>

      <div className="inz-grid">
        <h2 className="inz-grid-h" data-testid="features-headline">
          {site.featuresHeadline}
        </h2>
        {tiles.map((t, i) => (
          <div
            className="inz-tile reveal"
            key={t.n}
            style={{ transitionDelay: `${i * 70}ms` }}
          >
            <div className="inz-dot">{t.n}</div>
            <b>{t.title}</b>
            <span>{t.text}</span>
          </div>
        ))}
      </div>
      </main>

      <div className="inz-wide">
        <AppPreview />
      </div>

      <main className="inz-main inz-tail">
      <AnswerSection />
      <FaqSection />

      <p className="inz-fine">{site.fine}</p>

      <footer className="inz-footer">
        <span className="inz-tagline" data-testid="footer-tagline">
          {site.tagline}
        </span>
        <br />
        {site.company}
        <br />
        <Link
          to="/ingredients"
          onClick={() => track("click_ingredients_az", { label: "footer" })}
        >
          Ingredients A-Z
        </Link>{" "}
        {"\u00b7 "}
        <Link
          to="/ingredient"
          onClick={() => track("click_ingredient_blog", { label: "footer" })}
        >
          Ingredient blog
        </Link>{" "}
        {"\u00b7 "}
        <Link to="/contact">Contact</Link> {"\u00b7 "}
        <a
          className="inz-support-link"
          href="https://royalelite.support"
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => track("click_support_portal", { label: "footer" })}
          data-testid="support-portal-link"
        >
          Support
        </a>{" "}
        {"\u00b7 "}
        <Link to="/legal/privacy">Privacy</Link> {"\u00b7 "}
        <Link to="/legal/terms">Terms</Link> {"\u00b7 "}
        <Link to="/legal">All legal pages</Link> {"\u00b7 "}
        <Link to="/data-deletion">Delete my data</Link> {"\u00b7 "}
        <Link to="/admin/login" className="inz-admin-link" rel="nofollow">
          Admin
        </Link>
      </footer>
    </main>
    </>
  );
};

export default Landing;
